/**
 * TrialOnboardingTour — lightweight, in-house tour overlay for trialing users.
 *
 * Shows a sequence of dismissable hint cards anchored to key navigation items
 * so first-time trialing users know exactly where to click to test the app.
 *
 * Design rationale (vs. react-joyride / shepherd.js):
 *   • Only 4 steps — a 60kb library is overkill.
 *   • We need it to look ON-BRAND (amber accents, Tailwind), and joyride's
 *     theming is hard to match.
 *   • Tour anchors to elements by `data-tour-id` attributes rather than DOM
 *     queries, so it survives styling refactors.
 *
 * Trigger rules (updated for the 7-day CC-upfront trial):
 *   • Shown only to users whose subscription is in Stripe's `trialing` status
 *     (a paying customer with a trial-active subscription — card on file,
 *     trial not yet converted), AND who haven't dismissed the tour before.
 *   • Auto-hides once the tour is dismissed (localStorage flag).
 *   • Never shown to users with no subscription — those users see the
 *     paywall + "Start trial" CTAs on the dashboard instead.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ArrowRight, ArrowLeft, Sparkles, Search, MapPin, FileText, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth.jsx';

const STORAGE_KEY = 'qf-trial-tour-dismissed';
const STORAGE_KEY_LAST_STEP = 'qf-trial-tour-step';

// Each step describes WHAT to highlight and WHERE the user should navigate
// if the highlight target isn't on the current page.
const STEPS = [
    {
        id: 'welcome',
        title: '👋 Welcome — you\'re on your 7-day trial',
        body: 'Your card is on file but you won\'t be charged for 7 full days. Let\'s put every feature to work — starting with your first lead.',
        icon: Rocket,
        routeIfMissing: '/app/dashboard',
        targetId: null, // floating welcome card
    },
    {
        id: 'lead-finder',
        title: 'Step 1 — Open the Lead Finder',
        body: 'Click "Lead Finder" in the sidebar. You can search by ZIP code or look up any specific address.',
        icon: Search,
        targetId: 'nav-leads',
        routeIfMissing: '/app/leads',
    },
    {
        id: 'lookup',
        title: 'Step 2 — Try the "Lookup any address" tab',
        body: 'Quickest way to test: paste in your own home address (or a friend\'s). Start typing and pick a real address from the dropdown.',
        icon: MapPin,
        targetId: 'lead-finder-tab-address',
        routeIfMissing: '/app/leads',
    },
    {
        id: 'estimate',
        title: 'Step 3 — Click "Instant AI quote"',
        body: 'Once a lead is unlocked, hit this button to auto-generate a full quote (sqft, pitch, material, line-item costs) using AI Vision on the satellite image.',
        icon: Sparkles,
        targetId: 'lead-instant-quote-button',
        // No routeIfMissing — only relevant when on a LeadDetail page
    },
    {
        id: 'pdf',
        title: 'Step 4 — Download the PDF & hand-deliver',
        body: 'Edit anything that looks off (we left every field editable), then click "Download PDF" and you\'re done. Total time: ~60 seconds.',
        icon: FileText,
        targetId: 'estimate-pdf-button',
    },
];

export function TrialOnboardingTour() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [stepIndex, setStepIndex] = useState(0);
    const [open, setOpen] = useState(false);
    const [eligible, setEligible] = useState(false);
    const [anchorRect, setAnchorRect] = useState(null);
    const dismissTimerRef = useRef(null);

    // Decide eligibility: only trialing subscribers who haven't dismissed
    // the tour. Users with no subscription see the paywall instead.
    useEffect(() => {
        if (!user) { setOpen(false); return undefined; }
        const dismissed = localStorage.getItem(STORAGE_KEY);
        if (dismissed) { setOpen(false); return undefined; }

        let cancelled = false;
        (async () => {
            try {
                const r = await api.get('/billing/balance');
                const data = r.data?.credits || {};
                const isTrialing = data.subscription_status === 'trialing';
                if (cancelled) return;
                setEligible(isTrialing);
                if (isTrialing) {
                    // Resume from last completed step if user navigated mid-tour
                    const saved = parseInt(localStorage.getItem(STORAGE_KEY_LAST_STEP) || '0', 10);
                    setStepIndex(Math.min(saved, STEPS.length - 1));
                    // Tiny delay so DOM has settled before measuring anchor
                    dismissTimerRef.current = setTimeout(() => setOpen(true), 600);
                }
            } catch (_e) {
                /* ignore */
            }
        })();
        return () => {
            cancelled = true;
            if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
        };
    }, [user?.id]);

    // Persist step index so the tour resumes after navigation
    useEffect(() => {
        if (open) localStorage.setItem(STORAGE_KEY_LAST_STEP, String(stepIndex));
    }, [stepIndex, open]);

    // Measure the anchor element on each step / route change so the tooltip
    // floats next to the right button.
    const step = STEPS[stepIndex];
    const measureAnchor = useCallback(() => {
        if (!step?.targetId) { setAnchorRect(null); return; }
        const el = document.querySelector(`[data-testid="${step.targetId}"]`);
        if (!el) { setAnchorRect(null); return; }
        const r = el.getBoundingClientRect();
        setAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right });
        // Scroll target into view (smoothly)
        try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) { /* noop */ }
    }, [step?.targetId]);

    useEffect(() => {
        if (!open) return undefined;
        measureAnchor();
        const handler = () => measureAnchor();
        window.addEventListener('resize', handler);
        window.addEventListener('scroll', handler, true);
        // Re-measure after route transitions settle
        const t = setTimeout(measureAnchor, 400);
        return () => {
            window.removeEventListener('resize', handler);
            window.removeEventListener('scroll', handler, true);
            clearTimeout(t);
        };
    }, [open, location.pathname, measureAnchor]);

    if (!open || !eligible || !step) return null;

    const isLast = stepIndex >= STEPS.length - 1;

    const dismiss = () => {
        setOpen(false);
        localStorage.setItem(STORAGE_KEY, '1');
        localStorage.removeItem(STORAGE_KEY_LAST_STEP);
    };

    const next = () => {
        if (isLast) { dismiss(); return; }
        setStepIndex((i) => i + 1);
    };
    const prev = () => setStepIndex((i) => Math.max(0, i - 1));

    const goToTargetRoute = () => {
        if (step.routeIfMissing && location.pathname !== step.routeIfMissing) {
            navigate(step.routeIfMissing);
        }
    };

    const Icon = step.icon || Sparkles;

    // ── Tooltip card ─────────────────────────────────────────────────────────
    // Positioned next to the anchor (preferring right or below), or centred
    // if there's no anchor on the current page.
    const tooltipPos = anchorRect
        ? computeTooltipPosition(anchorRect)
        : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxWidth: 380 };

    return (
        <>
            {/* Backdrop spotlight — dims everything except the anchored element */}
            <div
                className="fixed inset-0 z-[60] pointer-events-none"
                aria-hidden
                data-testid="trial-tour-backdrop"
                style={{
                    background: anchorRect
                        ? `radial-gradient(circle at ${anchorRect.left + anchorRect.width / 2}px ${anchorRect.top + anchorRect.height / 2}px, transparent ${Math.max(anchorRect.width, anchorRect.height) * 0.75 + 30}px, rgba(15, 23, 42, 0.45) ${Math.max(anchorRect.width, anchorRect.height) * 0.75 + 60}px)`
                        : 'rgba(15, 23, 42, 0.40)',
                    transition: 'background 200ms ease-out',
                }}
            />

            {/* Spotlight ring around the anchor */}
            {anchorRect && (
                <div
                    className="fixed z-[61] pointer-events-none rounded-lg ring-4 ring-amber-400 ring-offset-2 animate-pulse-slow"
                    style={{
                        top: anchorRect.top - 4,
                        left: anchorRect.left - 4,
                        width: anchorRect.width + 8,
                        height: anchorRect.height + 8,
                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.0)',
                    }}
                    aria-hidden
                />
            )}

            {/* Tooltip card */}
            <div
                role="dialog"
                aria-label={`Trial tour step ${stepIndex + 1} of ${STEPS.length}`}
                className="fixed z-[62] w-[min(92vw,380px)] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-bottom-2"
                style={tooltipPos}
                data-testid="trial-tour-card"
            >
                <div className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="h-8 w-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                                <Icon className="h-4 w-4" />
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                {stepIndex + 1} / {STEPS.length}
                            </div>
                        </div>
                        <button
                            onClick={dismiss}
                            aria-label="Dismiss tour"
                            data-testid="trial-tour-dismiss"
                            className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md p-1 transition-colors"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <h3 className="font-display font-semibold text-slate-900 text-base sm:text-lg leading-snug">{step.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{step.body}</p>

                    {/* If the target isn't on this page, show a route-jump CTA */}
                    {!anchorRect && step.routeIfMissing && location.pathname !== step.routeIfMissing && (
                        <Button
                            onClick={goToTargetRoute}
                            size="sm"
                            className="mt-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold w-full"
                            data-testid="trial-tour-jump"
                        >
                            Take me there <ArrowRight className="h-3.5 w-3.5 ml-1" />
                        </Button>
                    )}

                    <div className="mt-4 flex items-center justify-between gap-2">
                        <div className="flex gap-1">
                            {STEPS.map((s, i) => (
                                <span
                                    key={s.id}
                                    className={`h-1.5 w-1.5 rounded-full ${i <= stepIndex ? 'bg-amber-500' : 'bg-slate-200'}`}
                                />
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            {stepIndex > 0 && (
                                <Button
                                    onClick={prev}
                                    variant="ghost"
                                    size="sm"
                                    className="text-slate-600 h-7 px-2"
                                    data-testid="trial-tour-prev"
                                >
                                    <ArrowLeft className="h-3.5 w-3.5 mr-0.5" /> Back
                                </Button>
                            )}
                            <Button
                                onClick={next}
                                size="sm"
                                className="bg-slate-900 hover:bg-slate-800 text-white h-7 px-3 text-xs"
                                data-testid="trial-tour-next"
                            >
                                {isLast ? 'Got it' : 'Next'} <ArrowRight className="h-3.5 w-3.5 ml-0.5" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// Compute a sensible tooltip position relative to the anchor element.
// Prefers right-of-anchor → below → above → left, whichever has space.
function computeTooltipPosition(rect) {
    const TT_WIDTH = 380;
    const TT_HEIGHT = 240;
    const PAD = 16;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

    // Try right of anchor
    if (rect.right + PAD + TT_WIDTH <= vw - PAD) {
        return {
            top: Math.max(PAD, Math.min(rect.top, vh - TT_HEIGHT - PAD)),
            left: rect.right + PAD,
        };
    }
    // Try below anchor
    if (rect.bottom + PAD + TT_HEIGHT <= vh - PAD) {
        return {
            top: rect.bottom + PAD,
            left: Math.max(PAD, Math.min(rect.left, vw - TT_WIDTH - PAD)),
        };
    }
    // Try above anchor
    if (rect.top - PAD - TT_HEIGHT >= PAD) {
        return {
            top: rect.top - PAD - TT_HEIGHT,
            left: Math.max(PAD, Math.min(rect.left, vw - TT_WIDTH - PAD)),
        };
    }
    // Fall back to left of anchor
    return {
        top: Math.max(PAD, Math.min(rect.top, vh - TT_HEIGHT - PAD)),
        left: Math.max(PAD, rect.left - PAD - TT_WIDTH),
    };
}
